import { useEffect } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useAuthStore, useGetUserSettings, useUserSettingsStore } from '@zendia/frontend-shared'
import { LoginForm } from '../../components/Login'
import { axiosRequest } from '../../utilities/axiosClient'
import { isOnboardingIncomplete } from '../../constants/onboarding.constants'

export const Route = createFileRoute('/_unauthenticated/login')({
  component: LoginComponent,
})

/**
 * Guards against a redirect target that points back at the login page, which would
 * otherwise bounce the user between login and itself forever.
 */
const getRedirectTarget = (redirect?: string): string =>
  !redirect || redirect === '/' || redirect.startsWith('/login') ? '/dashboard' : redirect

function LoginComponent() {
  const search = Route.useSearch()
  const navigate = useNavigate()
  const { authToken } = useAuthStore()
  const { settings: userSettings } = useUserSettingsStore()

  // Only worth asking for settings when there is a token to authenticate the
  // request with; an expired token simply fails and leaves the form on screen.
  const { isSuccess: hasLoadedUserSettings } = useGetUserSettings(axiosRequest, !!authToken)

  useEffect(() => {
    if (!authToken || !hasLoadedUserSettings) return

    if (isOnboardingIncomplete(userSettings?.onboardingStep)) {
      navigate({ to: '/onboarding' })
    } else {
      navigate({ to: getRedirectTarget(search.redirect) })
    }
  }, [authToken, hasLoadedUserSettings, userSettings, navigate, search.redirect])

  return <LoginForm redirectTo={getRedirectTarget(search.redirect)} notice={search.notice} />
}
